"use client";
import { useEffect, useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Instagram, Twitter, Facebook } from "lucide-react";
import { getFooter } from "@/lib/buttercms";
import Cookies from "js-cookie";

export default function Footer({ locale }) {
  const [footer, setFooter] = useState(null);
  const [error, setError] = useState(null);
  const [form, setForm] = useState({ name: "", email: "" });
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [ddID, setddID] = useState("");

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault(); // Prevent default form submission
    if (form.name && form.email && validateEmail(form.email)) {
      try {
        await subscribe(form.name, form.email, "en-ca"); // Assuming en-ca as locale
        setShowSuccessMessage(true);
      } catch (error) {
        console.error("Error during subscription:", error);
      }
    } else {
      console.warn("Invalid form data");
    }
  };

  const footer_bottom_links = [
    { name: "Refunds", link: "https://callia.com/en-ca/the-fine-print" },
    {
      name: "Terms & Conditions",
      link: "https://callia.com/en-ca/the-fine-print#terms-of-service",
    },
    { name: "Privacy Policy", link: "https://callia.com/en-ca/the-fine-print#privacy-policy" },
  ];

  useEffect(() => {
    const fetchFooterData = async () => {
      try {
        const response = await getFooter("en-ca");
        setFooter(response);
      } catch (err) {
        console.error("Error fetching footer data:", err);
      }
    };

    let attempts = 0;
    const poll = setInterval(() => {
      const clsk = Cookies.get("_clsk");
      if (clsk && typeof (window as any).clarity === "function") {
        clearInterval(poll);
        try {
          const sessionId = decodeURIComponent(clsk).split("^")[0];
          let stableId = Cookies.get("_cal_sid");
          if (!stableId) {
            stableId = sessionId;
            Cookies.set("_cal_sid", stableId, { expires: 1 / 48, path: "/" });
          }
          setddID(stableId);
          (window as any).clarity("identify", stableId, sessionId, "", stableId);
        } catch {
          setddID(Cookies.get("_cal_sid") || clsk.split("^")[0]);
        }
        return;
      }
      if (++attempts >= 30) clearInterval(poll);
    }, 1000);

    fetchFooterData();

    return () => clearInterval(poll);
  }, []);

  if (error) {
    return <div className="text-red-500">Error: {error}</div>;
  }

  return (
    <footer className="bg-white border-t">
      {footer && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <h3 className="text-lg font-semibold mb-4">{footer.header1}</h3>
              <ul className="space-y-2">
                {footer.footer_shop_desktop.map((item) => (
                  <li key={item.label}>
                    <a
                      href={item.url}
                      className="text-gray-600 hover:text-gray-900"
                    >
                      <p>{item.label}</p>
                    </a>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-4">{footer.header2}</h3>
              <ul className="space-y-2">
                {footer.footer_about_desktop.map((item) => (
                  <li key={item.label}>
                    <a
                      href={item.url}
                      className="text-gray-600 hover:text-gray-900"
                    >
                      <p>{item.label}</p>
                    </a>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-4">{footer.header3}</h3>
              <ul className="space-y-2">
                {footer.footer_care_desktop.map((item) => (
                  <li key={item.label}>
                    <a
                      href={item.url}
                      className="text-gray-600 hover:text-gray-900"
                    >
                      <p>{item.label}</p>
                    </a>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-4">
                Join 50,000+ people for new bouquets + sales
              </h3>
              <form onSubmit={handleSubmit} className="space-y-2">
                <Input
                  type="text"
                  name="name"
                  placeholder="Name"
                  value={form.name}
                  onChange={handleChange}
                  className="w-full"
                  required
                />
                <div className="flex">
                  <Input
                    type="email"
                    name="email"
                    placeholder="Email address"
                    value={form.email}
                    onChange={handleChange}
                    className="w-full"
                    required
                  />
                  <Button
                    type="submit"
                    className="ml-2 bg-primary text-secondary"
                  >
                    <p>SIGN UP</p>
                  </Button>
                </div>
                {showSuccessMessage && (
                  <p className="text-green-500">Successfully subscribed!</p>
                )}
              </form>
            </div>
          </div>
          <div className="mt-8 pt-4 border-t border-gray-200">
            <div className="flex flex-wrap justify-between items-center">
              {ddID && (
              <div className="flex items-center space-x-1">
                <span className="text-gray-600">Support #:</span>
                <Input type="text" value={ddID} readOnly className="w-20 h-8 overflow-x-auto" />
                <Button variant="ghost" size="icon" className="text-complementary hover:cursor-pointer hover:text-primary" onClick={() => navigator.clipboard.writeText(ddID)}>
                  <span className="sr-only">Copy support number</span>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="currentColor"
                    viewBox="0 0 16 16"
                    className="w-16 h-16"
                  >
                    <path d="M3.5 2a.5.5 0 0 0-.5.5v12a.5.5 0 0 0 .5.5h9a.5.5 0 0 0 .5-.5v-12a.5.5 0 0 0-.5-.5H12a.5.5 0 0 1 0-1h.5A1.5 1.5 0 0 1 14 2.5v12a1.5 1.5 0 0 1-1.5 1.5h-9A1.5 1.5 0 0 1 2 14.5v-12A1.5 1.5 0 0 1 3.5 1H4a.5.5 0 0 1 0 1h-.5Z" />
                    <path d="M10 .5a.5.5 0 0 0-.5-.5h-3a.5.5 0 0 0-.5.5.5.5 0 0 1-.5.5.5.5 0 0 0-.5.5V2a.5.5 0 0 0 .5.5h5A.5.5 0 0 0 11 2v-.5a.5.5 0 0 0-.5-.5.5.5 0 0 1-.5-.5Z" />
                  </svg>
                </Button>
              </div>
              )}
              <div className="flex items-center">
                <p className="text-gray-600">🇨🇦 EN-CA</p>
              </div>
              <div className="flex space-x-4">
                <a href="#" className="text-gray-400 hover:text-gray-500">
                  <Instagram className="h-6 w-6" />
                </a>
                <a href="#" className="text-gray-400 hover:text-gray-500">
                  <Twitter className="h-6 w-6" />
                </a>
                <a href="#" className="text-gray-400 hover:text-gray-500">
                  <Facebook className="h-6 w-6" />
                </a>
              </div>
            </div>
          </div>
          <div className="mt-4 pt-8 border-t border-gray-200 flex flex-wrap justify-between items-center">
            <p className="text-gray-500">&copy; 2026 Callia Inc</p>
            <p className="text-gray-500">1475 Dublin Ave, Winnipeg, MB R3E 3G8</p>
            <div className="flex space-x-6">
              {footer_bottom_links.map((item) => (
                <a
                  key={item.name}
                  href={item.link}
                  target="new"
                  className="text-gray-500 hover:text-gray-900 text-sm"
                >
                  <p>{item.name}</p>
                </a>
              ))}
            </div>
          </div>
        </div>
      )}
    </footer>
  );
}
